import * as Menu from '@radix-ui/react-dropdown-menu';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '@/api/error-text';
import { useSession } from '@/api/session';
import { de } from '@/i18n/de';
import { cn } from '@/shell/lib/cn';
import { Button } from '@/shell/ui/button';
import { InternalLink } from '@/shell/ui/internal-link';
import { useToast } from '@/shell/ui/toast';
import type { MoreItem } from './nav';

const ITEM =
  'flex min-h-11 cursor-pointer items-center rounded-control px-3 text-body text-ink outline-none data-[highlighted]:bg-surface-sunken sm:min-h-10';

type MoreMenuProps = {
  items: readonly MoreItem[];
  /** Untere Leiste: Menü öffnet nach oben, Auslöser mit Symbol über dem Wort. */
  stacked?: boolean;
  className?: string;
};

/**
 * Menü „Mehr“ (Radix, Tastatur und Vorlesegeräte): die übrigen Ziele, Beispieldaten ansehen bzw. zurück zu den
 * eigenen Daten, und Abmelden.
 */
export function MoreMenu({ items, stacked = false, className }: MoreMenuProps) {
  const session = useSession();
  const demo = session.me?.mode === 'demo';
  const demoAvailable = session.me?.demoAvailable ?? false;
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  /** Sitzungsaktionen laufen nacheinander; ein Fehler wird gemeldet (der Nutzer bleibt sonst ohne Rückmeldung). */
  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Menu.Root modal={false}>
      <Menu.Trigger asChild>
        {stacked ? (
          <button
            type="button"
            className={cn(
              'flex min-h-14 min-w-0 flex-auto flex-col items-center justify-center gap-0.5 px-1 text-fine font-semibold text-ink-muted',
              className,
            )}
          >
            <Ellipsis aria-hidden="true" className="size-6" />
            <span>{de.nav.mehr}</span>
          </button>
        ) : (
          <Button variant="quiet" className={className}>
            {de.nav.mehr}
            <Ellipsis aria-hidden="true" />
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align={stacked ? 'end' : 'end'}
          side={stacked ? 'top' : 'bottom'}
          sideOffset={8}
          collisionPadding={12}
          className="z-40 min-w-56 max-w-[calc(100vw-1.5rem)] rounded-surface border border-line bg-surface-block p-2 shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-150"
        >
          {items.map((item) => (
            <Menu.Item key={item.id} asChild>
              <InternalLink href={item.href} className={ITEM}>
                {item.label}
              </InternalLink>
            </Menu.Item>
          ))}
          <Menu.Separator className="my-2 h-px bg-line" />
          {demoAvailable ? (
            <Menu.Item
              className={ITEM}
              disabled={busy}
              onSelect={() => void run(() => session.setMode(demo ? 'live' : 'demo'))}
            >
              {demo ? de.nav.demoEnd : de.nav.demoStart}
            </Menu.Item>
          ) : null}
          <Menu.Item
            className={ITEM}
            disabled={busy}
            onSelect={() => void run(() => session.logout())}
          >
            {de.nav.logout}
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
